import { useState } from 'react';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Preset definitions ──────────────────────────────────────────────────────

const PRESETS = [
  // Scroll-triggered
  { id: 'fade-up',     label: 'Fade Up',      trigger: 'scroll',   cssDemo: 'translateY(8px)', desc: 'Rises up on scroll' },
  { id: 'fade-in',     label: 'Fade In',      trigger: 'scroll',   cssDemo: null,              desc: 'Fades in on scroll' },
  { id: 'slide-right', label: 'Slide Right',  trigger: 'scroll',   cssDemo: 'translateX(-8px)',desc: 'Slides from left' },
  { id: 'slide-left',  label: 'Slide Left',   trigger: 'scroll',   cssDemo: 'translateX(8px)', desc: 'Slides from right' },
  { id: 'scale-up',    label: 'Scale Up',     trigger: 'scroll',   cssDemo: 'scale(0.94)',     desc: 'Scales up on scroll' },
  { id: 'bounce-in',   label: 'Bounce In',    trigger: 'scroll',   cssDemo: 'translateY(6px)', desc: 'Bouncy entrance' },
  // Hover
  { id: 'hover-lift',  label: 'Hover Lift',   trigger: 'hover',    cssDemo: null,              desc: 'Lifts on hover' },
  { id: 'hover-glow',  label: 'Hover Glow',   trigger: 'hover',    cssDemo: null,              desc: 'Glows on hover' },
  // Entrance
  { id: 'pulse',       label: 'Pulse',        trigger: 'entrance', cssDemo: null,              desc: 'Subtle infinite pulse' },
];

const TRIGGER_LABELS = { scroll: 'Scroll', hover: 'Hover', entrance: 'Always' };
const TRIGGER_COLOR = { scroll: 'var(--orbis-blue)', hover: 'var(--orbis-amber)', entrance: 'var(--orbis-green)' };

// Minimal CSS demo shown while hovering a preset card
const DEMO_KEYFRAMES = `
@keyframes orbis-demo-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes orbis-demo-move { from { transform: var(--demo-from, none); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes orbis-demo-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
@keyframes orbis-demo-lift { 0%,100% { transform: translateY(0); box-shadow: none; } 50% { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.25); } }
@keyframes orbis-demo-glow { 0%,100% { box-shadow: none; } 50% { box-shadow: 0 0 0 3px rgba(184,204,223,0.55); } }
`;

function presetDemoStyle(preset, hovering) {
  if (!hovering) return {};
  const base = { animationDuration: '0.8s', animationFillMode: 'both' };
  if (preset.id === 'fade-in') return { ...base, animation: 'orbis-demo-fade 0.7s ease' };
  if (preset.id === 'pulse')   return { ...base, animation: 'orbis-demo-pulse 1.2s ease-in-out infinite' };
  if (preset.id === 'hover-lift') return { ...base, animation: 'orbis-demo-lift 1s ease-in-out infinite' };
  if (preset.id === 'hover-glow') return { ...base, animation: 'orbis-demo-glow 1s ease-in-out infinite' };
  if (preset.cssDemo) {
    return {
      ...base,
      '--demo-from': preset.cssDemo,
      animation: 'orbis-demo-move 0.7s ease',
    };
  }
  return { ...base, animation: 'orbis-demo-fade 0.7s ease' };
}

// ─── PresetCard ──────────────────────────────────────────────────────────────

function PresetCard({ preset, selected, applying, onClick }) {
  const [hovered, setHovered] = useState(false);
  const isLoading = applying === preset.id;

  return (
    <button
      onClick={() => !isLoading && onClick(preset.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={preset.desc}
      className={`relative flex flex-col items-center justify-center gap-1 rounded-lg border p-2 transition-all text-center
        ${selected === preset.id
          ? 'border-[var(--orbis-blue)] bg-[var(--orbis-blue)]/10'
          : 'border-[rgba(255,255,255,0.08)] bg-[var(--orbis-s2)] hover:border-[rgba(255,255,255,0.18)] hover:bg-[var(--orbis-s3)]'
        }`}
      style={{ minHeight: 56 }}
    >
      {/* Demo indicator dot */}
      <div
        className="w-5 h-5 rounded-md flex items-center justify-center"
        style={{
          backgroundColor: `${TRIGGER_COLOR[preset.trigger]}22`,
          ...presetDemoStyle(preset, hovered && !isLoading),
        }}
      >
        <span
          className="block w-2 h-2 rounded-full"
          style={{ backgroundColor: TRIGGER_COLOR[preset.trigger] }}
        />
      </div>

      <span className="text-[10px] font-medium leading-tight" style={{ color: 'var(--orbis-t2)' }}>
        {preset.label}
      </span>

      {/* Trigger badge */}
      <span className="text-[9px] leading-none" style={{ color: TRIGGER_COLOR[preset.trigger], opacity: 0.8 }}>
        {TRIGGER_LABELS[preset.trigger]}
      </span>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-[var(--orbis-bg)]/60">
          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--orbis-blue)' }} />
        </div>
      )}
      {selected === preset.id && !isLoading && (
        <div
          className="absolute top-1 right-1 w-3 h-3 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--orbis-blue)' }}
        >
          <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
            <path d="M1 2.5L2.8 4L6 1" stroke="var(--orbis-bg)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </button>
  );
}

// ─── AnimationPanel ──────────────────────────────────────────────────────────

export default function AnimationPanel({ selectedElement, projectId, currentPageSlug, reviewEditorState, onApplied }) {
  const cssPath = selectedElement?.cssPath || '';
  const animations = reviewEditorState?.animations || {};
  const currentPreset = animations[cssPath]?.preset || null;

  const [applying, setApplying] = useState(null); // preset id being applied

  async function applyPreset(presetId) {
    if (!cssPath) {
      toast.error('No element selected');
      return;
    }
    const isRemoving = presetId === currentPreset;
    const nextPreset = isRemoving ? '' : presetId;
    setApplying(isRemoving ? '__remove__' : presetId);
    try {
      const resp = await axios.post(
        `${API_URL}/magic/projects/${projectId}/set-animation`,
        {
          page_slug: currentPageSlug || 'home',
          css_path: cssPath,
          preset: nextPreset,
          delay: 0,
          duration: 0.6,
        }
      );
      toast.success(isRemoving ? 'Animation removed' : `Animation applied`);
      if (onApplied) onApplied(resp.data?.magic_state);
    } catch (err) {
      toast.error('Failed to apply animation');
    } finally {
      setApplying(null);
    }
  }

  return (
    <>
      {/* Inject demo keyframes once */}
      <style>{DEMO_KEYFRAMES}</style>

      <div className="space-y-2">
        {!cssPath && (
          <p className="text-[11px]" style={{ color: 'var(--orbis-t4)' }}>
            Select an element in the preview to add an animation.
          </p>
        )}

        {cssPath && (
          <>
            <div className="grid grid-cols-3 gap-1.5">
              {PRESETS.map(p => (
                <PresetCard
                  key={p.id}
                  preset={p}
                  selected={currentPreset}
                  applying={applying}
                  onClick={applyPreset}
                />
              ))}
            </div>

            {currentPreset && (
              <button
                onClick={() => applyPreset(currentPreset)}
                disabled={!!applying}
                className="w-full flex items-center justify-center gap-1.5 text-[11px] py-1 rounded-md transition-colors"
                style={{ color: 'var(--orbis-t4)' }}
              >
                <X className="w-3 h-3" />
                Remove animation
              </button>
            )}

            <p className="text-[10px] pt-0.5" style={{ color: 'var(--orbis-t4)' }}>
              Hover a preset to preview. Click to apply. Undo with ⌘Z.
            </p>
          </>
        )}
      </div>
    </>
  );
}
